import { mapExperienceCartItemToSummaryViews } from 'checkout/lib/utils/experiences/view'
import { excludeNullOrUndefined } from 'checkout/utils'
import { createSelector } from 'reselect'
import getExperienceItems from '../items/getExperienceItems'
import { getExperienceTimesKey } from 'lib/experiences/experienceUtils'
import config from 'constants/config'
import { sum, isNonNullable, arrayToMap, fillArray } from 'lib/array/arrayUtils'
import { incrementUuidV4 } from 'lib/string/uuidV4Utils'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import mapExperienceCartItemToTicketItemView from 'checkout/lib/utils/experiences/mapExperienceCartItemToTicketView'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { getCurrentIntl } from 'i18n/util/currentIntl'

function getTicketKey(ticket: App.ExperienceItemTicket, requiresTime?: boolean) {
  return `${ticket.id}-${ticket.date}-${requiresTime ? ticket.time : ''}`
}

function getCartTicketKey(ticket: App.Checkout.ExperienceItemTicket, time: string = '') {
  return `${ticket.ticketId}-${ticket.date}-${time}`
}

const getExperienceItemsView = createSelector(
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => state.experience.experiences,
  (state: App.State) => state.experience.experienceTimes,
  (state: App.State) => state.checkout.cart.currencyCode,
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getIsEnabledLuxPlusDiscountedExperiences(state),
  (state: App.State) => state.experience.experienceItemsValidation,
  (
    experienceItems,
    experiences,
    experienceTimes,
    currencyCode,
    existingOrder,
    isLuxPlusDiscountedExperiencesEnabled,
    itemsValidation,
  ): App.WithDataStatus<Array<App.Checkout.ExperienceItemView>> => {
    const intl = getCurrentIntl()
    const itemViews = experienceItems.map(cartItem => {
      const experience = experiences[cartItem.experienceId]

      const key = getExperienceTimesKey(cartItem.experienceId, cartItem.date, {
        currency: currencyCode,
        pickupPointId: cartItem.pickupPointId,
        redemptionLocationId: cartItem.redemptionLocationId,
        isBuyNowBookLater: cartItem.isBuyNowBookLater,
        ticketModesMap: cartItem.ticketModesMap,
        isGift: cartItem.isGift,
        orderId: existingOrder?.id,
      })

      const timeSlots = experienceTimes[cartItem.experienceId]?.[key]?.slots
      const hasTickets = timeSlots?.some(slot => slot.tickets.length)

      if (experience && hasTickets && timeSlots) {
        const requiresTime = experience.bookingType === 'CALENDAR-TIMESLOTS' && !cartItem.isBuyNowBookLater && !cartItem.isGift
        const ticketsByKey = arrayToMap(timeSlots.flatMap(slot => slot.tickets), ticket => getTicketKey(ticket, requiresTime))
        // Creates a per-ticket view (tickets * ticket count)
        const ticketViews = cartItem.tickets.flatMap(ticketItem => {
          const key = getCartTicketKey(ticketItem, requiresTime ? cartItem.time : undefined)
          const ticket = ticketsByKey.get(key)
          if (ticket) {
            return fillArray(ticketItem.count).map(index => ({
              item: ticketItem,
              ticket,
              orderItemId: ticketItem.orderItemIds?.[index],
              // only the first item has the actual
              hasLoyaltyDiscount: ticketItem.loyaltyTransferDiscount && index === 0,
            }))
          }
          return []
        }).map((ticketItem, index) => {
          const nextTransactionKey = incrementUuidV4(cartItem.transactionKey, index)
          const nextItemId = incrementUuidV4(cartItem.itemId, index)
          return mapExperienceCartItemToTicketItemView(
            ticketItem.ticket,
            nextTransactionKey,
            nextItemId,
            experience,
            {
              isBookDates: cartItem.isBookingBNBL,
              isAccomBundle: !!cartItem.isAccomBundle,
              orderItemId: ticketItem.orderItemId,
              hasLoyaltyDiscount: ticketItem.hasLoyaltyDiscount,
              isLuxPlusDiscountedExperiencesEnabled,
              ticketValidation: itemsValidation[ticketItem.orderItemId ?? nextItemId],
            },
          )
        })
        // now get the summary versions (This summerises to the ticket level)
        const ticketSummaryViews = cartItem.tickets.map(ticketItem => {
          const key = getCartTicketKey(ticketItem, requiresTime ? cartItem.time : undefined)
          const ticket = ticketsByKey.get(key)

          if (cartItem.isAccomBundle && ticket) {
            // For bundles, ignore retail prices
            ticket.price = ticketItem.price
            ticket.memberPrice = ticketItem.memberPrice
            ticket.discount = ticketItem.discount
            ticket.discounts.app.amount = 0
            ticket.discounts.app.percentage = 0
            ticket.discounts.base.amount = 0
            ticket.discounts.base.percentage = 0
          }

          const ticketView = ticket && ticketViews.find((ticketView) => ticketView.ticket.productId === ticket.productId)
          return mapExperienceCartItemToSummaryViews(
            ticketItem,
            ticket,
            {
              isBookDates: cartItem.isBookingBNBL,
              isAccomBundle: !!cartItem.isAccomBundle,
              ticketValidation: ticketView ? itemsValidation[ticketView.orderItemId ?? ticketView.item.itemId] : undefined,
            },
          )
        }).filter(isNonNullable)

        // calculate the totals price and member price
        const price = sum(ticketSummaryViews, tv => tv.price)
        const memberPrice = sum(ticketViews, tv => tv.totals.memberPrice)
        const memberValue = sum(ticketViews, tv => tv.totals.memberValue)

        const value = sum(ticketSummaryViews, tv => tv.value)
        const taxesAndFees = sum(ticketSummaryViews, tv => tv.taxesAndFees)

        const appPriceTickets = ticketSummaryViews.filter(tv => tv.discounts.app.amount > 0)
        const appDiscountAmount = sum(appPriceTickets, tv => tv.discounts.app.amount)
        const appDiscountPercentage = appDiscountAmount > 0 ? sum(appPriceTickets, tv => tv.discounts.app.percentage) / appPriceTickets.length : 0

        const isItemValid = ticketViews.every((ticketView) => !ticketView.unavailable)
        const validationError = ticketViews
          .map(ticketView => itemsValidation[ticketView.orderItemId ?? ticketView.item.itemId])
          .find(validation => validation && !validation.fetching && !validation.isValid && validation.error)?.error

        const itemView: App.Checkout.ExperienceItemView = {
          item: cartItem,
          id: cartItem.itemId,
          type: experience.productType,
          experience,
          experienceId: experience.id,
          primaryCategory: (experience.categories.find(cat => cat.level === 'parent') ?? experience.categories[0])?.name,
          categories: experience.categories,
          location: experience.location.name,
          latitude: experience.location.latitude,
          longitude: experience.location.longitude,
          timezone: experience.location.timezone,
          externalImage: experience.images[0],
          loyalty: {
            productType: getLoyaltyProductType(experience),
          },
          totals: {
            price,
            memberPrice,
            value,
            mobileAppDiscount: {
              amount: appDiscountAmount,
              percentage: appDiscountPercentage,
            },
            taxesAndFees,
            surcharge: 0,
            memberValue,
            extraGuestSurcharge: 0,
          },
          discount: getValueOffPercent(value, price),
          propertyFees: 0,
          title: experience.name,
          ticketViews,
          ticketSummaryViews,
          leExclusive: experience.leExclusive && config.BRAND === 'luxuryescapes',
          freeCancellation: experience.cancellationPolicies.length > 0,
          pickupPoint: cartItem.pickupPointId ? experience.pickupPoints.find(pp => pp.id === cartItem.pickupPointId) : undefined,
          language: cartItem.languageId ? experience.languages.find(l => l.id === cartItem.languageId) : undefined,
          ticketModesMap: cartItem.ticketModesMap,
          bookingDate: experience.hasCalendar ? cartItem.date : undefined,
          bookingTime: cartItem.time,
          cancellationPolicies: experience.cancellationPolicies,
          cancellationInfo: experience.copy.cancellationInfo,
          hasCalendar: experience.hasCalendar,
          hasTimeslots: experience.hasTimeslots,
          hideTimeSlots: experience.hideTimeSlots,
          designation: intl.formatMessage({
            id: '7ApklZCqFPcNUpa1FIKV',
            defaultMessage: 'Experiences',
            description: 'Item type designation label for an experience booking in the order summary display',
          }),
          redemptionLocation: cartItem.redemptionLocationId ?
              experience.redemptionLocations.find(rl => rl.id === cartItem.redemptionLocationId) : undefined,
          isBuyNowBookLater: cartItem.isBuyNowBookLater,
          isGift: cartItem.isGift,
          isBookDates: cartItem.isBookingBNBL,
          expirationDate: experience.expirationDate,
          bookByDate: experience.bookByDate,
          isValid: isItemValid,
          validationError,
          isLoyaltyTransfer: cartItem.isLoyaltyTransfer,
          vendor: experience.vendor?.name ?? '',
          provider: experience.provider ?? '',
        }

        return itemView
      }
    })

    return {
      hasRequiredData: itemViews.every(Boolean),
      data: itemViews.filter(excludeNullOrUndefined),
    }
  },
)

export default getExperienceItemsView
